// user-offer-details-component.ts
import { Component, computed, effect, inject, input, LOCALE_ID, OnInit, signal } from '@angular/core';
import { OffersService } from '../../../services/offers/offers.service';
import { CurrentUserService } from '../../../services/current-user.service';
import { ErrorData } from '../../../utils/error-data/error-data';
import { finalize, forkJoin } from 'rxjs';
import { ConfirmationDialog } from '../../../shared/ui/confirmation-dialog/confirmation-dialog';
import { Router } from '@angular/router';
import { Loading } from '../../../utils/loading/loading';
import { ImageCarousel } from '../../admin-dashboard-component/product-details/utils/image-carousel/image-carousel';
import { SafeUrl } from '@angular/platform-browser';
import { ProductsFileMgt } from '../../../services/product/file-management/products.file-mgt.service';
import {
  OneBundleTypeDto,
  OneOfferTypeDto,
} from '../../../pages/user-dashboard/user-offer-details/user-offer-details.model';
import { FilterItemSearch } from '../../../shared/ui/shared-filters/filter-item-search/filter-item-search';
import { BundleService } from '../../../services/bundle/bundle';
import { ProductService } from '../../../services/product/product.service';
import { Stepper } from '../../../shared/ui/stepper/stepper';
import { SubmitButton } from '../../../shared/ui/Buttons/submit-button/submit-button';
import { PharmacistCartService } from '../../../services/cart/cart.pharmacist';
import { PharmacyCreditService } from '../../../services/pharmacy-credit/pharmacy-credit';
import { CartDto } from '../../../pages/admin-dashboard/cart-management/admin-cart.model';
import { Error } from '../../public-user-component/auth/shared/error/error';
import { SuccessfulNotification } from '../../../utils/successful-notification/successful-notification';
import { OfferProductSelector } from '../../admin-dashboard-component/admin-cart-component/admin-cart-details-component/select-offer/offer-product-selector/offer-product-selector';
import { EnrichedOfferItem } from '../../admin-dashboard-component/admin-cart-component/admin-cart-details-component/select-offer/available-offers/available-offers';
import { ProductCardComponent } from '../products/product-list-component/product-card-component/product-card-component';
import { MedicineIcon } from '../../../shared/ui/icons/product-card/medicine-icon/medicine-icon';

@Component({
  selector: 'app-user-offer-details-component',
  imports: [
    ErrorData,
    ConfirmationDialog,
    Loading,
    ImageCarousel,
    FilterItemSearch,
    Stepper,
    SubmitButton,
    Error,
    SuccessfulNotification,
    OfferProductSelector,
    ProductCardComponent,
    MedicineIcon,
  ],
  templateUrl: './user-offer-details-component.html',
  styleUrl: './user-offer-details-component.scss',
})
export class UserOfferDetailsComponent implements OnInit {
  private offerService = inject(OffersService);
  private productService = inject(ProductService);
  private bundleService = inject(BundleService);
  private pharmacistCartService = inject(PharmacistCartService);
  private fileMgt = inject(ProductsFileMgt);
  private pharmacyCreditService = inject(PharmacyCreditService);

  selectedPharmacy = computed(() => this.pharmacyCreditService.selectedPharmacy());
  isPharmacySuspended = computed(() => this.pharmacyCreditService.isSelectedPharmacySuspended());

  offerBundleType = input.required<'offer' | 'bundle'>();

  private currentUserService = inject(CurrentUserService);
  private locale = inject(LOCALE_ID);
  private router = inject(Router);

  isArabic = computed(() => this.locale.startsWith('ar'));

  offerId = input.required<string>();

  offerData = signal<OneOfferTypeDto | null>(null);
  bundleData = signal<OneBundleTypeDto | null>(null);

  loadingOffer = signal(false);
  errorOffer = signal('');
  unAuthorized = signal(false);

  offerImageUrl = signal<{ url: SafeUrl | string; isImage: boolean } | undefined>(undefined);
  offerImages = signal<{ url: SafeUrl | string; isImage: boolean }[]>([]);

  bundleImageUrl = signal<{ url: SafeUrl | string; isImage: boolean } | undefined>(undefined);
  bundleImages = signal<{ url: SafeUrl | string; isImage: boolean }[]>([]);

  isLoadingImages = signal(false);
  imagePreview = signal<SafeUrl | string>('/assets/product.png');

  productSearch = signal('');
  filteredProducts = signal<OneOfferTypeDto['products'] | null>(null);
  isFilter = signal(false);

  itemQuantity = signal('1');
  errorQty = signal('');

  isAddingToCart = signal(false);
  isErrorAddToCart = signal('');

  isSuccessOfferAddToCart = signal(false);
  isSuccessBundleAddToCart = signal(false);

  bundleProductsFlat = computed(() => this.bundleData()?.products ?? []);

  enrichedOfferForSelector = computed<EnrichedOfferItem | null>(() => {
    const offer = this.offerData();

    if (!offer) return null;

    return {
      ...(offer as any),
      selectedQuantity: +this.itemQuantity() || 1,
    } as EnrichedOfferItem;
  });

  loadOffer() {
    this.loadingOffer.set(true);
    this.errorOffer.set('');
    this.isFilter.set(false);

    this.offerService
      .getOneCustomerOffer(this.offerId())
      .pipe(finalize(() => this.loadingOffer.set(false)))
      .subscribe({
        next: (res: OneOfferTypeDto) => {
          this.offerData.set(res);

          this.offerImageUrl.set({
            url: '',
            isImage: false,
          });

          this.isLoadingImages.set(true);

          this.fetchImage(res.profileImageFids[0])
            .pipe(finalize(() => this.isLoadingImages.set(false)))
            .subscribe({
              next: (img) => this.offerImageUrl.set(img),
              error: () =>
                this.offerImageUrl.set({
                  url: '',
                  isImage: false,
                }),
            });

          if (res.profileImageFids && res.profileImageFids.length > 1) {
            const requests = res.profileImageFids.slice(1).map((fid) => this.fetchImage(fid));

            forkJoin(requests).subscribe({
              next: (images: any) => this.offerImages.set(images),
              error: () => this.offerImages.set([]),
            });
          }
        },
        error: (err) => {
          if (err.status === 403) {
            this.unAuthorized.set(true);
          }

          this.errorOffer.set(
            this.isArabic() ? err.error.messageAr || 'حدث بعض الخطأ' : err.error.messageEn || 'some error occured!',
          );
        },
      });
  }

  loadBundle() {
    this.loadingOffer.set(true);
    this.errorOffer.set('');
    this.isFilter.set(false);

    this.bundleService
      .getOneCustomerBundle(this.offerId())
      .pipe(finalize(() => this.loadingOffer.set(false)))
      .subscribe({
        next: (res: OneBundleTypeDto) => {
          this.bundleData.set(res);

          this.bundleImageUrl.set({
            url: '',
            isImage: false,
          });

          this.isLoadingImages.set(true);

          this.fetchImage(res.profileImageFid)
            .pipe(finalize(() => this.isLoadingImages.set(false)))
            .subscribe({
              next: (img) => this.bundleImageUrl.set(img),
              error: () =>
                this.bundleImageUrl.set({
                  url: '',
                  isImage: false,
                }),
            });

          if (res.imagesFids && res.imagesFids.length > 1) {
            const requests = res.imagesFids.slice(1).map((fid) => this.fetchImage(fid));

            forkJoin(requests).subscribe({
              next: (images: any) => this.bundleImages.set(images),
              error: () => this.bundleImages.set([]),
            });
          }
        },
        error: (err) => {
          if (err.status === 403) {
            this.unAuthorized.set(true);
          }

          this.errorOffer.set(
            this.isArabic() ? err.error.messageAr || 'حدث بعض الخطأ' : err.error.messageEn || 'some error occured!',
          );
        },
      });
  }

  searchOfferProduct(offerId: string, quickSearch: string) {
    this.isFilter.set(true);

    const filterOfferType = this.offerData()?.offerType.id === '1' ? 'DISCOUNT' : 'BxGy';

    this.productService
      .getAllProductsByFilter({
        quickSearch,
        offerDiscountId: filterOfferType === 'DISCOUNT' ? offerId : undefined,
        offerBxGyId: filterOfferType === 'BxGy' ? offerId : undefined,
      })
      .subscribe({
        next: (res) => {
          this.filteredProducts.set(
            res.list.map((item) => ({
              id: item.id.toString(),
              titleAr: item.titleAr!,
              titleEn: item.titleEn!,
              customerPrice: item.customerPrice?.toString()!,
              businessPrice: item.businessPrice?.toString()!,
              mfgDate: item.mfgDate!,
              expDate: item.expireDate!,
              batchNumber: item.barcode!,
              profileImageFid: item.profileImageFid!,
            })),
          );
        },
        error: () => {},
      });
  }

  constructor() {
    effect(() => {
      const search = this.productSearch();
      const id = this.offerData()?.id;

      if (!id) return;

      if (!search) {
        this.isFilter.set(false);
        this.filteredProducts.set(null);
        return;
      }

      this.searchOfferProduct(id, search);
    });
  }

  ngOnInit() {
    if (this.offerBundleType() === 'offer') {
      this.loadOffer();
    }

    if (this.offerBundleType() === 'bundle') {
      this.loadBundle();
    }
  }

  redirectToLogin() {
    this.router.navigate(['/login']);
    this.currentUserService.logout();
  }

  get groupedProducts(): any[] {
    if (this.offerBundleType() === 'bundle') {
      const products = this.bundleData()?.products ?? [];
      const map = new Map<string, any[]>();

      for (const p of products) {
        const key = p.product.titleEn;
        const group = map.get(key) ?? [];
        group.push(p);
        map.set(key, group);
      }

      return Array.from(map.entries()).map(([key, items]) => ({
        key,
        keyAr: items[0]?.product?.titleAr ?? key,
        items,
      }));
    }

    const products = this.filteredProducts() ?? this.offerData()?.products ?? [];
    const map = new Map<string, any[]>();

    for (const p of products) {
      const group = map.get(p.titleEn) ?? [];
      group.push(p);
      map.set(p.titleEn, group);
    }

    return Array.from(map.entries()).map(([key, items]) => ({
      key,
      keyAr: items[0]?.titleAr ?? key,
      items,
    }));
  }

  private fetchImage(fid: string) {
    return this.fileMgt.getImageUrl(fid);
  }

  increaseQty() {
    this.errorQty.set('');

    if (+this.itemQuantity() === 100) {
      this.errorQty.set(this.isArabic() ? 'يجب ألا تتجاوز القيمة 100' : 'Value must not exceed 100');
    }

    if (+this.itemQuantity() < 100) {
      this.itemQuantity.set((+this.itemQuantity() + 1).toString());
    }
  }

  decreaseQty() {
    this.errorQty.set('');

    if (+this.itemQuantity() === 1) {
      this.errorQty.set(this.isArabic() ? 'يجب أن تكون القيمة أكبر من صفر' : 'Value must be greater than 0');
    }

    if (+this.itemQuantity() > 1) {
      this.itemQuantity.set((+this.itemQuantity() - 1).toString());
    }
  }

  qtyChange(value: string) {
    this.errorQty.set('');

    if (+value > 100) {
      this.errorQty.set(this.isArabic() ? 'يجب ألا تتجاوز القيمة 100' : 'Value must not exceed 100');
    }

    if (+value < 1) {
      this.errorQty.set(this.isArabic() ? 'يجب أن تكون القيمة أكبر من صفر' : 'Value must be greater than 0');
    }

    this.itemQuantity.set(value);
  }

  errorQuantity() {
    const qty = Number(this.itemQuantity());

    if (!Number.isInteger(qty)) {
      this.errorQty.set(this.isArabic() ? 'يرجى إدخال رقمًا صحيحًا' : 'Please enter a valid number.');

      return;
    }
  }

  onCancelOfferSelection() {
    this.router.navigate(['/user/offers']);
  }

  onConfirmOfferProducts() {
    this.isSuccessOfferAddToCart.set(true);

    setTimeout(() => {
      this.isSuccessOfferAddToCart.set(false);
    }, 2000);
  }

  onAddToCart() {
    if (this.offerBundleType() !== 'bundle' || this.isPharmacySuspended()) {
      return;
    }

    if (+this.itemQuantity() === 0) {
      this.errorQty.set(this.isArabic() ? 'يجب أن تكون القيمة أكبر من صفر' : 'Value must be greater than 0');
      return;
    }

    const dto: CartDto = {
      items: [
        {
          type: 'bundle',
          bundleId: +this.offerId(),
          quantity: +this.itemQuantity(),
        },
      ],
    };

    this.isAddingToCart.set(true);
    this.isErrorAddToCart.set('');

    this.pharmacistCartService
      .addItemToCart('bundle', this.selectedPharmacy()!.id, dto)
      .pipe(
        finalize(() => {
          this.isAddingToCart.set(false);
        }),
      )
      .subscribe({
        next: () => {
          this.isSuccessBundleAddToCart.set(true);

          setTimeout(() => {
            this.isSuccessBundleAddToCart.set(false);
          }, 2000);
        },
        error: (err) => {
          this.isErrorAddToCart.set(
            this.isArabic() ? err.messageAr || 'حدث بعض الخطأ!' : err.messageEn || 'some error occured!',
          );
        },
      });
  }
}
